import React from 'react';
import { Assumptions, CompModel } from '../types/compensation';
import { formatDkk, formatPercent } from '../utils/format';
import { vestingSummary } from '../utils/compensation';
interface ProposalPanelProps {
  model: CompModel;
  assumptions: Assumptions;
}
export function ProposalPanel({
  model,
  assumptions
}: ProposalPanelProps) {
  const cash = [{
    label: 'Monthly fixed salary',
    value: `${formatDkk(model.fixedMonthly)}`
  }, {
    label: 'Annual guaranteed salary',
    value: formatDkk(model.fixedAnnual)
  }, {
    label: 'Expected annual commission at target',
    value: formatDkk(model.expectedCommission)
  }];
  const terms = [{
    label: 'Existing customer commission',
    value: formatPercent(model.existingRate)
  }, {
    label: 'New business commission',
    value: formatPercent(model.newBusinessRate)
  }, {
    label: 'Indicative warrant / equity',
    value: formatPercent(model.ownershipPercent, 2)
  }];
  return <section aria-label="Your proposed Snakker package" className="overflow-hidden rounded-card border border-paper/20 bg-ink-850" data-id="element-150" data-mp="1kif0lirsu2dk">
      <div className="border-b border-line px-6 py-6 lg:px-10 lg:py-8" data-id="element-151" data-mp="1pforz3yr0o41">
        <h2 className="text-2xl font-semibold tracking-tight text-paper lg:text-3xl" data-id="element-152" data-mp="1bjkxranvkfrk">
          Your proposed Snakker package
        </h2>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-paper-muted" data-id="element-153" data-mp="23967mdobdgos">
          You choose where you want the value: guaranteed cash, performance
          upside or long-term ownership.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-px bg-line lg:grid-cols-3" data-id="element-154" data-mp="1uhe3sltsp5t6">
        <div className="bg-ink-900 p-6 lg:p-8" data-id="element-155" data-mp="1uf1ka48e2ne0">
          <p className="text-[11px] uppercase tracking-[0.18em] text-security" data-id="element-156" data-mp="1yhehg492lcz6">
            Cash
          </p>
          <dl className="mt-4 divide-y divide-line-soft" data-id="element-157" data-mp="2m78twkph8ziw">
            {cash.map(row => <div key={row.label} className="py-3" data-id="element-158" data-mp="2qzk1m9qglpm0">
                <dt className="text-sm text-paper-muted" data-id="element-159" data-mp="30ur23t290ca2">{row.label}</dt>
                <dd className="tnum mt-1 text-lg font-semibold text-paper" data-id="element-160" data-mp="30smwn6opb57o">
                  {row.value}
                </dd>
              </div>)}
          </dl>
          <div className="mt-4 border-t border-line pt-4" data-id="element-161" data-mp="2rsrkayjet462">
            <p className="text-[11px] uppercase tracking-[0.16em] text-paper-dim" data-id="element-162" data-mp="2mv80lj3xzz6q">
              Cash OTE
            </p>
            <p className="tnum mt-1 text-[30px] font-semibold leading-none tracking-tightest text-paper" data-id="element-163" data-mp="2hwwthg5182s7">
              {formatDkk(model.cashOte)}
            </p>
          </div>
        </div>

        <div className="bg-ink-900 p-6 lg:p-8" data-id="element-164" data-mp="292ryckiv856v">
          <p className="text-[11px] uppercase tracking-[0.18em] text-performance" data-id="element-165" data-mp="291gu6jlicvf8">
            Commercial terms
          </p>
          <dl className="mt-4 divide-y divide-line-soft" data-id="element-166" data-mp="1z541np1p8nm2">
            {terms.map(row => <div key={row.label} className="py-3" data-id="element-167" data-mp="1u6aoxyu8l0my">
                <dt className="text-sm text-paper-muted" data-id="element-168" data-mp="1u6u63dc2bkzx">{row.label}</dt>
                <dd className="tnum mt-1 text-lg font-semibold text-paper" data-id="element-169" data-mp="1k359cvazu95i">
                  {row.value}
                </dd>
              </div>)}
          </dl>
          <p className="mt-4 border-t border-line pt-4 text-xs leading-relaxed text-paper-dim" data-id="element-170" data-mp="1u0t6tszzhurd">
            Commission is paid on realised revenue. Rates apply from the first
            krone of incremental and new-logo revenue.
          </p>
        </div>

        <div className="bg-ink-900 p-6 lg:p-8" data-id="element-171" data-mp="11dp8g6veceok">
          <p className="text-[11px] uppercase tracking-[0.18em] text-ownership" data-id="element-172" data-mp="16afncqvgt48e">
            Ownership
          </p>
          <div className="mt-4" data-id="element-173" data-mp="1b9sc5h5wcldm">
            <p className="text-sm text-paper-muted" data-id="element-174" data-mp="1b8qz575blot9">Vesting terms</p>
            <p className="mt-1 text-sm leading-relaxed text-paper" data-id="element-175" data-mp="1g1tsbe66qw8x">
              {vestingSummary(assumptions)}
            </p>
          </div>
          {assumptions.performanceWarrants ? <div className="mt-4 border-t border-line-soft pt-4" data-id="element-176" data-mp="njo5t21bq7ix">
              <p className="text-sm text-paper-muted" data-id="element-177" data-mp="2p9n14ao7f0sc">
                Performance warrants on milestones
              </p>
              <ul className="mt-2 space-y-1.5" data-id="element-178" data-mp="2kbbq3wm6ak8j">
                {assumptions.milestones.map(m => <li key={m.id} className="flex items-baseline justify-between gap-3 text-xs" data-id="element-179" data-mp="2yzq1ivxnnh0p">
                    <span className="text-paper" data-id="element-180" data-mp="2u6wyqdlqyir9">{m.label}</span>
                    <span className="tnum shrink-0 text-paper-dim" data-id="element-181" data-mp="37ytpoeboimm2">
                      {m.grant.trim() === '' ? 'To be agreed' : m.grant}
                    </span>
                  </li>)}
              </ul>
            </div> : null}
          <p className="mt-4 border-t border-line pt-4 text-xs leading-relaxed text-paper-dim" data-id="element-182" data-mp="3cyprc6wwm1yg">
            Indicative option/warrant allocation — actual economic value depends
            on strike price, vesting, dilution, tax treatment and exit value.
            Equity is not included in OTE.
          </p>
        </div>
      </div>
    </section>;
}